import { html } from 'htm/preact'
import { useState, useEffect } from 'preact/hooks'
import Router from 'preact-router'
import { Header } from './components/Header.js'
import { NewsList } from './components/NewsList.js'
import { SourcesSidebar } from './components/SourcesSidebar.js'
import { Sources } from './components/Sources.js'
import { Search } from './components/Search.js'
import { AdminDashboard } from './components/AdminDashboard.js'
import { AdminDashboardTest } from './components/AdminDashboard.test.js'
import { LoginModal } from './components/LoginModal.js'
import { api, authService } from './services/api.js'

export function App() {
  const [sources, setSources] = useState([])
  const [selectedSources, setSelectedSources] = useState(null)
  const [darkMode, setDarkMode] = useState(false)
  const [allArticles, setAllArticles] = useState([])
  const [currentPath, setCurrentPath] = useState('/')
  
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(authService.isAuthenticated())
  const [user, setUser] = useState(authService.getUser())
  const [showLoginModal, setShowLoginModal] = useState(false)

  useEffect(() => {
    // Load dark mode preference
    const savedDarkMode = localStorage.getItem('darkMode') === 'true'
    setDarkMode(savedDarkMode)
    if (savedDarkMode) {
      document.documentElement.classList.add('dark')
    }
    // Load sources and initially select all active sources
    loadSources()
    // Load all articles for the sidebar stats
    loadAllArticles()
  }, [])

  useEffect(() => {
    // Auto-select all active sources only on the very first load (when selectedSources is still null).
    // Uses functional updater to always read the latest state, avoiding stale-closure races
    // between the multiple concurrent loadSources calls that fire on mount.
    if (sources.length > 0) {
      setSelectedSources(prev => {
        if (prev === null) {
          return sources.filter(source => source.is_active).map(source => source.id)
        }
        return prev
      })
    }
  }, [sources])
  
  const loadSources = async () => {
    try {
      const response = await api.get('/sources/')
      setSources(response.data.sources)
    } catch (error) {
      console.error('Error loading sources:', error)
    }
  }

  const loadAllArticles = async () => {
    try {
      const response = await api.get('/articles/', { params: { per_page: 50 } })
      setAllArticles(response.data.articles || [])
      // Also refresh sources to update article counts
      loadSources()
    } catch (error) {
      console.error('Error loading articles:', error)
    }
  }

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode
    setDarkMode(newDarkMode)
    localStorage.setItem('darkMode', newDarkMode.toString())
    document.documentElement.classList.toggle('dark', newDarkMode)
  }

  const toggleSource = (sourceId) => {
    setSelectedSources(prev => {
      if (prev.includes(sourceId)) {
        return prev.filter(id => id !== sourceId)
      } else {
        return [...prev, sourceId]
      }
    })
  }

  const toggleAllSources = () => {
    const activeSources = sources.filter(source => source.is_active).map(source => source.id)
    
    if ((selectedSources || []).length === activeSources.length) {
      // If all active sources are selected, deselect all
      setSelectedSources([])
    } else {
      // Select all active sources
      setSelectedSources(activeSources)
    }
  }

  const handleRouteChange = (e) => {
    setCurrentPath(e.url)
  }

  // Auth handlers
  const handleLoginClick = () => {
    setShowLoginModal(true)
  }

  const handleLoginSuccess = () => {
    setIsAuthenticated(true)
    setUser(authService.getUser())
  }

  const handleLogout = () => {
    authService.logout()
    setIsAuthenticated(false)
    setUser(null)
  }

  // Don't show sidebar on admin pages
  const shouldShowSidebar = !currentPath.startsWith('/admin')

  return html`
    <div class="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      <${Header} 
        darkMode=${darkMode} 
        toggleDarkMode=${toggleDarkMode}
        selectedSources=${selectedSources}
        sources=${sources}
        isAuthenticated=${isAuthenticated}
        user=${user}
        onLoginClick=${handleLoginClick}
        onLogout=${handleLogout}
      />
      
      <div class="flex flex-grow overflow-hidden">
        <!-- Sources sidebar - moved to left -->
        ${shouldShowSidebar ? html`
          <${SourcesSidebar}
            sources=${sources}
            selectedSources=${selectedSources}
            onToggleSource=${toggleSource}
            onToggleAllSources=${toggleAllSources}
            articles=${allArticles}
          />
        ` : null}
        
        <!-- Main content area -->
        <main class="flex-grow overflow-y-auto">
          <${Router} onChange=${handleRouteChange}>
            <${NewsList} 
              path="/" 
              selectedSources=${selectedSources}
              sources=${sources}
              setSources=${setSources}
              onArticlesUpdated=${loadAllArticles}
              isAuthenticated=${isAuthenticated}
              onLoginClick=${handleLoginClick}
            />
            <${Search} 
              path="/search/:query?" 
              isAuthenticated=${isAuthenticated}
              onLoginClick=${handleLoginClick}
            />
            <${Sources} 
              path="/sources" 
              sources=${sources}
              setSources=${setSources}
              selectedSources=${selectedSources}
              toggleSource=${toggleSource}
            />
            <${AdminDashboard} path="/admin" />
          </Router>
        </main>
      </div>

      <!-- Login Modal -->
      <${LoginModal}
        isOpen=${showLoginModal}
        onClose=${() => setShowLoginModal(false)}
        onLoginSuccess=${handleLoginSuccess}
      />
    </div>
  `
}
